import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/app/Header";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CheckCircle, WarningCircle, Plug, ArrowsClockwise, MicrosoftOutlookLogo,
} from "@phosphor-icons/react";

const StatusPill = ({ status }) => {
  if (status === "healthy") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md" data-testid={`health-pill-${status}`}>
        <CheckCircle weight="fill" className="w-3.5 h-3.5" />
        Healthy
      </span>
    );
  }
  if (status === "expired") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-md" data-testid={`health-pill-${status}`}>
        <WarningCircle weight="fill" className="w-3.5 h-3.5" />
        Reconnect required
      </span>
    );
  }
  if (status === "not_connected") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md" data-testid={`health-pill-${status}`}>
        <Plug weight="bold" className="w-3.5 h-3.5" />
        Not connected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md" data-testid={`health-pill-${status}`}>
      Inactive
    </span>
  );
};

/**
 * `/settings/integrations/health` — firm-wide Outlook connection
 * health dashboard. Admin-only.
 *
 * Each row is one solicitor; status pills make revoked connections
 * obvious at a glance. We poll every 30s while the tab is foregrounded
 * so a fresh disconnect surfaces here within half a minute without the
 * user having to click "Refresh".
 */
export default function IntegrationHealthPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && !(user.role === "solicitor" && user.is_admin)) {
      toast.error("Admin solicitor access required");
      navigate("/settings/integrations");
    }
  }, [user, navigate]);

  const fetchHealth = useCallback(async () => {
    try {
      const [{ data }, perfRes] = await Promise.all([
        API.get("/admin/outlook/health"),
        API.get("/admin/perf/slow-requests").catch(() => ({ data: null })),
      ]);
      setData(data);
      setPerf(perfRes.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load connection health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!(user?.role === "solicitor" && user?.is_admin)) return;
    fetchHealth();
    // Poll every 30s while the tab is visible so disconnects surface
    // here without the admin having to manually refresh.
    const id = setInterval(() => {
      if (!document.hidden) fetchHealth();
    }, 30000);
    return () => clearInterval(id);
  }, [user, fetchHealth]);

  const formatWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const counts = data?.counts || {};
  const rows = data?.rows || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="integration-health-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Outlook Connection Health
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Firm-wide view of every solicitor&apos;s Outlook connection. Refreshes every 30 seconds.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setLoading(true); fetchHealth(); }}
              data-testid="health-refresh-btn"
            >
              <ArrowsClockwise weight="bold" className="w-3.5 h-3.5 mr-1.5" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/settings/integrations")}
              data-testid="health-back-btn"
            >
              Back to Integrations
            </Button>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="stat-healthy">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Healthy</div>
            <div className="text-2xl font-bold text-emerald-700 mt-1">{counts.healthy || 0}</div>
          </div>
          <div className="bg-white border border-red-200 rounded-lg p-4" data-testid="stat-expired">
            <div className="text-xs text-red-600 uppercase tracking-wide font-semibold">Reconnect required</div>
            <div className="text-2xl font-bold text-red-700 mt-1">{counts.expired || 0}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="stat-not-connected">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Not connected</div>
            <div className="text-2xl font-bold text-slate-700 mt-1">{counts.not_connected || 0}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="stat-inactive">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Inactive users</div>
            <div className="text-2xl font-bold text-slate-700 mt-1">{counts.inactive || 0}</div>
          </div>
        </div>

        {/* Performance watchdog tile — counts requests that exceeded
            5s in the last hour / 24h. Red when something's regressing,
            green when everything's snappy. Top offending paths are
            collapsed under a details disclosure to keep the page tidy. */}
        {perf && (
          <details
            className={`mb-6 rounded-lg border ${perf.last_hour_count > 0 ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}
            data-testid="perf-watchdog">
            <summary className="cursor-pointer list-none p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <div className={`text-xs uppercase tracking-wide font-semibold ${perf.last_hour_count > 0 ? "text-amber-700" : "text-slate-500"}`}>
                    Slow requests, last hour
                  </div>
                  <div className={`text-2xl font-bold mt-1 ${perf.last_hour_count > 0 ? "text-amber-800" : "text-emerald-700"}`} data-testid="perf-last-hour">
                    {perf.last_hour_count}
                  </div>
                </div>
                <div className="border-l border-slate-200 pl-4">
                  <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">Last 24h</div>
                  <div className="text-2xl font-bold mt-1 text-slate-700" data-testid="perf-last-24h">
                    {perf.last_24h_count}
                  </div>
                </div>
                <div className="border-l border-slate-200 pl-4 text-[11px] text-slate-500 leading-snug max-w-md">
                  Any request that takes longer than {perf.threshold_seconds}s on the backend is logged here.
                  Catches event-loop starvation <em>before</em> Cloudflare gives up with a 520.
                </div>
              </div>
              <span className="text-xs text-slate-400">
                {perf.top_paths_24h.length > 0 ? `${perf.top_paths_24h.length} path${perf.top_paths_24h.length === 1 ? "" : "s"} — click to expand` : ""}
              </span>
            </summary>
            {perf.top_paths_24h.length > 0 && (
              <div className="border-t border-slate-200 px-4 py-3 bg-white">
                <table className="w-full text-xs" data-testid="perf-top-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="py-1.5 font-semibold">Path</th>
                      <th className="py-1.5 font-semibold text-right">Hits</th>
                      <th className="py-1.5 font-semibold text-right">Avg</th>
                      <th className="py-1.5 font-semibold text-right">Worst</th>
                      <th className="py-1.5 font-semibold text-right">Last seen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {perf.top_paths_24h.map((row, i) => (
                      <tr key={i} data-testid={`perf-row-${i}`}>
                        <td className="py-1.5 font-mono text-slate-700">
                          <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded mr-1.5">{row.method}</span>
                          {row.path}
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-slate-700">{row.count}</td>
                        <td className="py-1.5 text-right tabular-nums text-slate-500">{(row.avg_ms / 1000).toFixed(1)}s</td>
                        <td className="py-1.5 text-right tabular-nums text-amber-700 font-semibold">{(row.max_ms / 1000).toFixed(1)}s</td>
                        <td className="py-1.5 text-right tabular-nums text-slate-500">{formatWhen(row.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </details>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          {loading && !data ? (
            <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-500">No solicitors found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="health-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                    <th className="px-4 py-2.5 font-medium">Solicitor</th>
                    <th className="px-4 py-2.5 font-medium">Mailbox</th>
                    <th className="px-4 py-2.5 font-medium">Status</th>
                    <th className="px-4 py-2.5 font-medium">Last synced</th>
                    <th className="px-4 py-2.5 font-medium">Cached emails</th>
                    <th className="px-4 py-2.5 font-medium">Last error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.user_id} className={r.status === "expired" ? "bg-red-50/40" : ""} data-testid={`health-row-${r.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 flex items-center gap-1.5">
                          {r.name || r.user_email}
                          {r.is_admin && (
                            <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded uppercase tracking-wide">Admin</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{r.user_email}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {r.mailbox_email ? (
                          <span className="inline-flex items-center gap-1.5">
                            <MicrosoftOutlookLogo weight="fill" className="w-3.5 h-3.5" style={{ color: "#0078d4" }} />
                            {r.mailbox_email}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatWhen(r.last_synced_at)}</td>
                      <td className="px-4 py-3 text-slate-700 tabular-nums">{r.cache_message_count?.toLocaleString?.() ?? 0}</td>
                      <td className="px-4 py-3 max-w-xs">
                        {r.last_refresh_error ? (
                          <div className="text-xs">
                            <div className="font-medium text-red-700">{r.last_refresh_error}</div>
                            {r.last_refresh_error_description && (
                              <div className="text-slate-500 mt-0.5 line-clamp-2" title={r.last_refresh_error_description}>
                                {r.last_refresh_error_description}
                              </div>
                            )}
                          </div>
                        ) : r.last_error ? (
                          <div className="text-xs text-amber-700">{r.last_error}</div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-400 mt-3">
          Tip: when a row shows <em>Reconnect required</em>, that solicitor needs to sign in to ConveyTrack and visit
          {" "}<em>Settings → Integrations</em> to disconnect and reconnect Outlook. An alert email was also sent to
          {" "}all admin solicitors when the connection was first revoked.
        </p>
      </main>
    </div>
  );
}
